import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Menu, PanelLeft } from 'lucide-react';
import { AppIcon } from '../LucideIcon';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { SkipLink } from '@ravenappdev/raven-ui';
import { useAuth } from '../../contexts/AuthContext';
import PnidNavigationRail from './PnidNavigationRail';
import { readStoredCollapsed, storeCollapsed } from './railState';
import PnidToolTheme from './PnidToolTheme';
import '../../../node_modules/@ravenappdev/raven-ui/dist/raven-ui.css';
import '../../styles/raven-foundation.css';
import './product-shell.css';

function accountLabel(userGroup?: string) {
  if (!userGroup) return 'Account unavailable';
  if (userGroup === 'sme_demo_group') return 'SME Demo account';
  return `${userGroup.replace(/[-_]+/g, ' ')} account`;
}

/**
 * Frame chrome buttons. Deliberately not RavenIconButton — that is skinned for
 * light surfaces and reads as a hole punched in the gradient. `size="small"`
 * is load-bearing: the rail's coarse-pointer 44px floor keys off
 * `.MuiIconButton-sizeSmall`.
 */
function FrameIconButton({ label, icon, onClick, ...rest }: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  'aria-expanded'?: boolean;
}) {
  return (
    <Tooltip title={label}>
      <IconButton
        className="pnid-frame-btn"
        size="small"
        aria-label={label}
        onClick={onClick}
        {...rest}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
}

function ShellFrame({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useAuth();
  /* Collapse state lives here, not in the rail: the toggle sits in the rail
     slot's head row above the rail, and the shell sizes both from one width. */
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);

  /* Below lg (1200px) collapse is forced regardless of the stored choice; at
     or above it the stored choice applies, and that defaults to collapsed.
     Crossing the threshold never writes storage, so widening the window
     restores whatever the user last chose. */
  useEffect(() => {
    if (compact || typeof window.matchMedia !== 'function') return undefined;
    const narrow = window.matchMedia('(max-width: 1199.95px)');
    const apply = () => setCollapsed(narrow.matches ? true : readStoredCollapsed());
    apply();
    narrow.addEventListener('change', apply);
    return () => narrow.removeEventListener('change', apply);
  }, [compact]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      storeCollapsed(next);
      return next;
    });
  }, []);

  /* The frame paints to the window edge, so the body behind it has to match or
     an overscroll bounce shows index.html's grey. Restored on unmount, because
     /login mounts the theme without the shell. */
  useEffect(() => {
    const previous = document.body.style.backgroundColor;
    const backdrop = getComputedStyle(document.documentElement)
      .getPropertyValue('--raven-shell-backdrop').trim();
    if (backdrop) document.body.style.backgroundColor = backdrop;
    return () => {
      document.body.style.backgroundColor = previous;
    };
  }, []);

  if (compact) {
    return (
      <div className="pnid-product-shell pnid-product-shell--compact">
        <SkipLink targetId="pnid-main-stage">Skip to workspace</SkipLink>
        <header className="pnid-compact-header">
          <FrameIconButton
            label="Open navigation"
            icon={<AppIcon icon={Menu} size={20} />}
            onClick={() => setDrawerOpen(true)}
          />
          <div className="pnid-compact-header-copy">
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {accountLabel(user?.user_group)}
            </Typography>
            <Typography variant="caption" noWrap component="div">
              {user?.username || 'Signed in'}
            </Typography>
          </div>
        </header>
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          slotProps={{ paper: { className: 'pnid-frame-drawer', sx: { width: 260 } } }}
        >
          {/* The Drawer is already an overlay, so its rail never collapses. */}
          <PnidNavigationRail collapsed={false} onNavigated={() => setDrawerOpen(false)} />
        </Drawer>
        <main id="pnid-main-stage" className="pnid-compact-content" tabIndex={-1}>
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="pnid-product-shell" data-rail={collapsed ? 'collapsed' : 'expanded'}>
      <SkipLink targetId="pnid-main-stage">Skip to workspace</SkipLink>

      <div className="pnid-frame-body">
        <div className="pnid-frame-rail-slot">
          <div className="pnid-frame-rail-head">
            <FrameIconButton
              label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              aria-expanded={!collapsed}
              icon={<AppIcon icon={PanelLeft} size={20} />}
              onClick={toggleCollapsed}
            />
          </div>
          <PnidNavigationRail collapsed={collapsed} />
        </div>
        <div className="pnid-frame-panel-wrap">
          <main className="pnid-frame-panel">
            <div id="pnid-main-stage" className="pnid-main-stage" tabIndex={-1}>
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default function PnidWorkspaceShell({ children }: { children: ReactNode }) {
  return (
    <PnidToolTheme>
      <ShellFrame>{children}</ShellFrame>
    </PnidToolTheme>
  );
}
